import {
  CollaborationCaret as TiptapCollaborationCaret,
  type CollaborationCaretOptions as TiptapCollaborationCaretOptions,
} from '@tiptap/extension-collaboration-caret'
import { EditorState, Plugin, Selection } from '@tiptap/pm/state'
import {
  Decoration,
  DecorationAttrs,
  DecorationSet,
  type EditorView,
} from '@tiptap/pm/view'
import {
  defaultAwarenessStateFilter,
  relativePositionToAbsolutePosition,
  yCursorPlugin,
  yCursorPluginKey,
  ySyncPluginKey,
} from '@tiptap/y-tiptap'
import * as Y from 'yjs'
import { textContrast } from '@opallabs/gem'

type AwarenessUser = {
  name: string | null | undefined
  color: string | null | undefined
}
type NonNullAwarenessUser = {
  [P in keyof AwarenessUser]-?: NonNullable<AwarenessUser[P]>
}
type AwarenessRecord = {
  cursor: Selection | null | undefined
  user: AwarenessUser | null | undefined
} & Record<string, unknown>

interface CollaborationCaretOptions
  extends Omit<TiptapCollaborationCaretOptions, 'render'> {
  render: (
    user: NonNullAwarenessUser,
    clientId: number,
    view: EditorView,
    getPos: () => number | undefined
  ) => Element
}

const awarenessStatesToArray = (
  states: Map<number, Record<string, unknown>>
) => {
  return Array.from(states.entries()).map(([key, value]) => {
    return {
      clientId: key,
      ...(typeof value.user === 'object' ? value.user : {}),
    }
  })
}

export const createDecorations = (
  state: EditorState,
  awareness: {
    getStates: () => Map<number, AwarenessRecord>
  },
  awarenessFilter: (
    currentClientId: number,
    userClientId: number,
    aw: AwarenessRecord
  ) => boolean,
  createCursor: (
    user: NonNullAwarenessUser,
    clientId: number,
    view: EditorView,
    getPos: () => number | undefined
  ) => Element,
  createSelection: (
    user: NonNullAwarenessUser,
    clientId: number
  ) => DecorationAttrs
) => {
  const ystate = ySyncPluginKey.getState(state)
  const y = ystate.doc
  const decorations: Decoration[] = []
  if (
    ystate.snapshot != null ||
    ystate.prevSnapshot != null ||
    ystate.binding.mapping.size === 0
  ) {
    // do not render cursors while snapshot is active
    return DecorationSet.create(state.doc, [])
  }
  awareness.getStates().forEach((aw, clientId) => {
    if (!awarenessFilter(y.clientID, clientId, aw)) {
      return
    }

    if (aw.cursor == null) {
      return
    }

    const user = aw.user || ({} as AwarenessUser)
    if (user.color == null) {
      user.color = '#ffa500'
    }
    if (user.name == null) {
      user.name = `User: ${clientId}`
    }

    let anchor = relativePositionToAbsolutePosition(
      y,
      ystate.type,
      Y.createRelativePositionFromJSON(aw.cursor.anchor),
      ystate.binding.mapping
    )
    let head = relativePositionToAbsolutePosition(
      y,
      ystate.type,
      Y.createRelativePositionFromJSON(aw.cursor.head),
      ystate.binding.mapping
    )
    if (anchor !== null && head !== null) {
      const maxsize = Math.max(state.doc.content.size - 1, 0)
      anchor = Math.min(anchor, maxsize)
      head = Math.min(head, maxsize)
      decorations.push(
        Decoration.widget(
          head,
          (view, getPos) =>
            createCursor(user as NonNullAwarenessUser, clientId, view, getPos),
          {
            key: `${clientId}-${Math.trunc(head)}`,
            side: 10,
          }
        )
      )
      const from = Math.min(anchor, head)
      const to = Math.max(anchor, head)
      decorations.push(
        Decoration.inline(
          from,
          to,
          createSelection(user as NonNullAwarenessUser, clientId),
          {
            inclusiveEnd: true,
            inclusiveStart: false,
          }
        )
      )
    }
  })
  return DecorationSet.create(state.doc, decorations)
}

const CollaborationCaret =
  TiptapCollaborationCaret.extend<CollaborationCaretOptions>({
    addOptions() {
      return {
        ...(this.parent?.() as TiptapCollaborationCaretOptions),
        render: (user, _clientId, view, getPos) => {
          const cursor = document.createElement('span')
          cursor.classList.add('collaboration-cursor__caret')
          cursor.setAttribute('style', `border-color: ${user.color}`)

          const pos = getPos()
          if (pos != null) {
            const viewRect = view.dom.getBoundingClientRect()
            const cursorRect = view.coordsAtPos(pos)

            // Flip cursor label if cursor is in the right half of the document
            if (cursorRect.left - viewRect.left > viewRect.width / 2) {
              cursor.classList.add('collaboration-cursor__flipped')
            }
          }

          const label = document.createElement('div')
          label.classList.add('collaboration-cursor__label')
          label.setAttribute(
            'style',
            `background-color: ${user.color}; color: ${textContrast(user.color)}`
          )

          label.insertBefore(document.createTextNode(user.name), null)
          cursor.insertBefore(label, null)
          return cursor
        },
      }
    },

    addProseMirrorPlugins() {
      const awareness = (() => {
        this.options.provider.awareness.setLocalStateField(
          'user',
          this.options.user
        )

        this.storage.users = awarenessStatesToArray(
          this.options.provider.awareness.states
        )

        this.options.provider.awareness.on('update', () => {
          this.storage.users = awarenessStatesToArray(
            this.options.provider.awareness.states
          )
        })

        return this.options.provider.awareness
      })()

      const yCursor = yCursorPlugin(awareness, {}) as Plugin<DecorationSet>

      yCursor.spec.state = {
        init: (_, state) => {
          return createDecorations(
            state,
            awareness,
            defaultAwarenessStateFilter,
            this.options.render,
            this.options.selectionRender
          )
        },
        apply: (tr, prevState, _oldState, newState) => {
          const ystate = ySyncPluginKey.getState(newState)
          const yCursorState = tr.getMeta(yCursorPluginKey)
          if (
            (ystate && ystate.isChangeOrigin) ||
            (yCursorState && yCursorState.awarenessUpdated)
          ) {
            return createDecorations(
              newState,
              awareness,
              defaultAwarenessStateFilter,
              this.options.render,
              this.options.selectionRender
            )
          }
          return prevState.map(tr.mapping, tr.doc)
        },
      }

      return [yCursor]
    },
  })

export { CollaborationCaret, type CollaborationCaretOptions }
